import { TableBody, TableCell, TableRow } from "@mui/material";
import { BaseTable } from "../../../components/table";
import { BaseTablePropsType } from "../../../types/container.type";
import { formatAmountDisplay } from "../../../helpers/formatAmountDisplay";
import { useQuery } from "@tanstack/react-query";
import Cookies from "universal-cookie";
import { retrieveUserProfileService } from "../../../utils/api/usermanagement/profile/retrieveUser";
import { retrieveInstitutionByIdService } from "../../../utils/outbound/institution/retrieveInstitutionById";
import { retrieveCurrencyByIdService } from "../../../utils/outbound/currency/retrieveCurrencyById";
import { useMemo } from "react";

export const IncomeStatementReportTable: React.FC<BaseTablePropsType> = ({
	rows,
}) => {
	const cookies = new Cookies();
	const TOKEN = cookies.getAll().TOKEN;

	const { data: transactingTeller } = useQuery({
		queryKey: ["transactingTeller", TOKEN],
		queryFn: async () => {
			return await retrieveUserProfileService(TOKEN);
		},
		enabled: !!TOKEN,
	});

	const { data: currency } = useQuery({
		queryKey: [
			"transactingCurrencySymbol",
			transactingTeller?._id,
			transactingTeller?.institution,
		],
		queryFn: async () => {
			if (!transactingTeller) return null;
			const institution = await retrieveInstitutionByIdService(
				transactingTeller._id,
				transactingTeller.institution,
			);
			const currencyDetails = await retrieveCurrencyByIdService(
				transactingTeller._id,
				institution?.currency,
			);
			return currencyDetails;
		},
		enabled: !!transactingTeller,
	});

	const incomeStatementTableHeaders = ["REVENUE", `${currency?.symbol ?? ""}`];

	const orderedRows = useMemo(() => {
		if (!rows?.length) return [];
		const revenues = rows.filter((row) => row.glType === "400");
		const expenses = rows.filter((row) => row.glType === "500");

		const totalRevenue = revenues.reduce(
			(sum, row) => sum + (row.balance ?? 0),
			0,
		);
		const totalExpense = expenses.reduce(
			(sum, row) => sum + (row.balance ?? 0),
			0,
		);
		const surplusOrLoss = totalRevenue - totalExpense;

		return [
			...revenues.map((r) => ({ ...r, type: "data" })),
			{
				type: "total",
				label: "TOTAL REVENUE",
				value: totalRevenue,
			},
			...(expenses.length
				? [
						{
							type: "divider",
							label: "OPERATING EXPENSE",
						} as const,
					]
				: []),
			...expenses.map((e) => ({ ...e, type: "data" })),
			{
				type: "total",
				label: "TOTAL EXPENSE",
				value: totalExpense,
			},
			{
				type: "surplus",
				label: "SURPLUS / (LOSS) FOR THE PERIOD",
				value: surplusOrLoss,
			},
		];
	}, [rows]);

	return (
		<BaseTable
			headers={orderedRows.length > 0 ? incomeStatementTableHeaders : []}
		>
			<TableBody>
				{orderedRows.map((row: Record<string, any>, index) => {
					switch (row.type) {
						case "divider":
							return (
								<TableRow key={`divider-${index}`} className="divider-row">
									<TableCell colSpan={2}>{row.label}</TableCell>
								</TableRow>
							);
						case "total":
							return (
								<TableRow key={`total-${index}`} className="total-row">
									<TableCell>{row.label}</TableCell>
									<TableCell>{formatAmountDisplay(row.value)}</TableCell>
								</TableRow>
							);
						case "surplus":
							return (
								<TableRow key={`surplus-${index}`} className="surplus-row">
									<TableCell>{row.label}</TableCell>
									<TableCell>{formatAmountDisplay(row.value)}</TableCell>
								</TableRow>
							);
						default:
							return (
								<TableRow key={index}>
									<TableCell>
										{row.glName?.replace(/\b\w/g, (c: string) =>
											c.toUpperCase(),
										)}
									</TableCell>
									<TableCell>{formatAmountDisplay(row.balance ?? 0)}</TableCell>
								</TableRow>
							);
					}
				})}
			</TableBody>
		</BaseTable>
	);
};
